export const COOKIE_CONSENT_KEY = 'cookieConsent';
export const COOKIE_CONSENT_VERSION_KEY = 'cookieConsentVersion';
export const COOKIE_CONSENT_UPDATED_AT_KEY = 'cookieConsentUpdatedAt';
export const COOKIE_PREFERENCES_KEY = 'cookiePreferences';

export const COOKIE_CONSENT_VERSION = '2026-07-17_privacy_terms_v3';
export const COOKIE_SETTINGS_EVENT = 'open-cookie-settings';
export const COOKIE_PREFERENCES_UPDATED_EVENT = 'cookie-preferences-updated';

export const DEFAULT_COOKIE_PREFERENCES = Object.freeze({
  essential: true,
  analytics: false,
  advertising: false
});

const hasWindow = () => typeof window !== 'undefined';

export const isGlobalPrivacyControlEnabled = () => (
  hasWindow() && window.navigator?.globalPrivacyControl === true
);

const parseJson = (rawValue, fallback = null) => {
  try {
    return JSON.parse(String(rawValue || ''));
  } catch {
    return fallback;
  }
};

const normalizePreferences = (value) => {
  const input = value && typeof value === 'object' ? value : {};
  return {
    essential: true,
    analytics: Boolean(input.analytics),
    advertising: isGlobalPrivacyControlEnabled() ? false : Boolean(input.advertising)
  };
};

const parseLegacyStatus = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'accepted') {
    return {
      essential: true,
      analytics: true,
      advertising: true
    };
  }
  if (normalized === 'rejected') {
    return {
      essential: true,
      analytics: false,
      advertising: false
    };
  }
  return { ...DEFAULT_COOKIE_PREFERENCES };
};

const deriveConsentStatus = (preferences) => {
  const prefs = normalizePreferences(preferences);
  if (prefs.analytics && prefs.advertising) return 'accepted';
  if (!prefs.analytics && !prefs.advertising) return 'rejected';
  return 'customized';
};

export const readCookieConsentState = () => {
  if (!hasWindow()) {
    return {
      status: 'unknown',
      version: '',
      updatedAt: '',
      hasCurrentVersion: false,
      preferences: { ...DEFAULT_COOKIE_PREFERENCES }
    };
  }

  let status = 'unknown';
  let version = '';
  let updatedAt = '';
  let parsedPreferences = null;

  try {
    status = localStorage.getItem(COOKIE_CONSENT_KEY) || 'unknown';
    version = localStorage.getItem(COOKIE_CONSENT_VERSION_KEY) || '';
    updatedAt = localStorage.getItem(COOKIE_CONSENT_UPDATED_AT_KEY) || '';
    parsedPreferences = parseJson(localStorage.getItem(COOKIE_PREFERENCES_KEY), null);
  } catch {
    // Ignore storage access issues and fallback to safe defaults.
  }

  const hasCurrentVersion = version === COOKIE_CONSENT_VERSION;
  const preferences = normalizePreferences(
    parsedPreferences || parseLegacyStatus(status)
  );

  return {
    status,
    version,
    updatedAt,
    hasCurrentVersion,
    preferences
  };
};

export const writeCookieConsentState = (nextPreferences, options = {}) => {
  const { source = 'cookie-banner' } = options;
  const preferences = normalizePreferences(nextPreferences);
  const status = deriveConsentStatus(preferences);
  const updatedAt = new Date().toISOString();
  let persisted = false;
  let errorCode = null;

  if (hasWindow()) {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, status);
      localStorage.setItem(COOKIE_CONSENT_VERSION_KEY, COOKIE_CONSENT_VERSION);
      localStorage.setItem(COOKIE_CONSENT_UPDATED_AT_KEY, updatedAt);
      localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));

      const storedPreferences = parseJson(localStorage.getItem(COOKIE_PREFERENCES_KEY), null);
      persisted = localStorage.getItem(COOKIE_CONSENT_KEY) === status
        && localStorage.getItem(COOKIE_CONSENT_VERSION_KEY) === COOKIE_CONSENT_VERSION
        && localStorage.getItem(COOKIE_CONSENT_UPDATED_AT_KEY) === updatedAt
        && Boolean(storedPreferences)
        && storedPreferences.essential === preferences.essential
        && storedPreferences.analytics === preferences.analytics
        && storedPreferences.advertising === preferences.advertising;

      if (!persisted) {
        errorCode = 'storage_verification_failed';
      }
    } catch {
      errorCode = 'storage_write_failed';
    }

    if (persisted) {
      try {
        window.dispatchEvent(new CustomEvent(COOKIE_PREFERENCES_UPDATED_EVENT, {
          detail: {
            status,
            version: COOKIE_CONSENT_VERSION,
            updatedAt,
            preferences,
            source
          }
        }));
      } catch {
        // Persistence succeeded; the return value remains the source of truth for the caller.
      }
    }
  } else {
    errorCode = 'storage_unavailable';
  }

  return {
    status,
    version: COOKIE_CONSENT_VERSION,
    updatedAt,
    hasCurrentVersion: persisted,
    preferences,
    persisted,
    errorCode
  };
};

export const isAnalyticsConsentGranted = () => readCookieConsentState().preferences.analytics;
export const isAdvertisingConsentGranted = () => readCookieConsentState().preferences.advertising;

export const openCookieSettings = () => {
  if (!hasWindow()) return;
  window.dispatchEvent(new CustomEvent(COOKIE_SETTINGS_EVENT));
};
